import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { api } from "../lib/api";
import LocalStorePicker from "./LocalStorePicker";
import ExportButtons from "./ExportButtons";
import { downloadCSV, downloadPDF, fmt } from "../lib/exports";
import { useSort } from "../lib/useSort";
import { SortHeader } from "./SortControls";
import PeriodBadge from "./PeriodBadge";

function fmtMoney(v) {
  const n = Number(v || 0);
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

const PERIODS = [
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "quarter", label: "Quarter" },
  { key: "year", label: "Year" },
];

const PERIOD_HINT = {
  week: "SKUs added to Lightspeed in the last 7 days",
  month: "SKUs added to Lightspeed in the last 30 days",
  quarter: "SKUs added to Lightspeed in the last 90 days",
  year: "SKUs added to Lightspeed in the last 12 months",
};

export default function NewArrivals({ initialOutletIds }) {
  const [outletIds, setOutletIds] = useState(initialOutletIds || []);
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState({ rows: [], total_new_products: 0, counts: {} });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    // Always scope to genuinely net-new product lines. New variants of existing
    // products are intentionally excluded — they're variant refreshes, not new SKUs.
    // Sort-by is done client-side; the backend simply returns the top new SKUs
    // ranked by revenue and we let the user reorder them here.
    const params = { period, sort_by: "revenue", scope: "net_new", limit: 10 };
    if (outletIds.length > 0) params.outlet_ids = outletIds.join(",");
    api.get("/products/new-arrivals", { params })
      .then((r) => { if (!cancel) setData(r.data || { rows: [], counts: {} }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [outletIds.join(","), period]);

  const rows = data.rows || [];
  const counts = data.counts || { net_new: 0, variants: 0 };

  const sort = useSort("revenue", "desc", {
    sku_count: (r) => r.sku_count,
    units: (r) => r.units,
    avg_price: (r) => r.avg_price,
    revenue: (r) => r.revenue,
  });
  const sortedRows = sort.apply(rows);
  const maxSortVal = Math.max(1, ...sortedRows.map((r) => (sort.sortKey === "revenue" ? r.revenue : sort.sortKey === "units" ? r.units : sort.sortKey === "avg_price" ? r.avg_price : r.sku_count)));

  const buildExport = () => {
    const columns = [
      { key: "n", label: "#", align: "right" },
      { key: "name", label: "Product" },
      { key: "category", label: "Category" },
      { key: "created_at", label: "Created" },
      { key: "sku_count", label: "New SKUs", align: "right" },
      { key: "units", label: "Units", align: "right" },
      { key: "avg_price", label: "Avg $", align: "right" },
      { key: "revenue", label: "Revenue", align: "right" },
    ];
    const flat = sortedRows.map((r, i) => ({
      n: i + 1,
      name: r.name,
      category: r.category || "",
      created_at: r.created_at || "",
      sku_count: r.sku_count,
      units: fmt.int(r.units),
      avg_price: `$${r.avg_price}`,
      revenue: fmt.money0(r.revenue),
    }));
    return {
      filenameBase: `new-arrivals-${period}`,
      meta: { title: "New product performance", subtitle: `${PERIOD_HINT[period]} · sorted by ${sort.sortKey} · ${sort.sortDir}` },
      columns,
      rows: flat,
    };
  };

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-4 md:p-6 min-w-0" data-testid="new-arrivals">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
        <div className="bm-eyebrow inline-flex items-center gap-1">
          <Sparkles size={11} /> Table 4 · New SKUs in Lightspeed
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period toggle */}
          <div className="flex border border-[#0a0a0a]" role="tablist" aria-label="Period" data-testid="new-arrivals-period">
            {PERIODS.map((p, i) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                data-testid={`new-arrivals-period-${p.key}`}
                className={`px-2 py-1 bm-eyebrow ${i > 0 ? "border-l border-[#0a0a0a]" : ""} ${
                  period === p.key ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <LocalStorePicker value={outletIds} onChange={setOutletIds} testId="new-arrivals-stores" />
        </div>
      </div>
      <h3 className="bm-headline text-3xl mb-1 text-emerald-800">New product performance.</h3>
      <PeriodBadge label={PERIOD_HINT[period].replace("SKUs added to Lightspeed in the ", "").replace(/^l/, "L") + " (to today)"} note="not tied to the date picker" className="mb-2" />
      <p className="bm-eyebrow text-neutral-500 mb-1">
        Top 10 net-new product lines added to Lightspeed in the last {period} · Printed Tees & variant refreshes excluded
      </p>
      <p className="bm-eyebrow text-neutral-400 mb-6">
        {PERIOD_HINT[period]} ·
        <span className="text-emerald-700"> {counts.net_new || 0} net-new</span>
        {counts.variants ? <span className="text-neutral-400"> · {counts.variants} variant refreshes hidden</span> : null}
      </p>

      <div className="flex justify-end mb-3">
        <ExportButtons
          disabled={!rows.length}
          testIdPrefix="new-arrivals-export"
          onCSV={() => { const e = buildExport(); downloadCSV(e.filenameBase, e.columns, e.rows); }}
          onPDF={() => { const e = buildExport(); downloadPDF(e.filenameBase, e.meta, e.columns, e.rows); }}
        />
      </div>

      {loading && rows.length === 0 && <div className="text-sm text-neutral-500">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-sm text-neutral-600">
          No net-new product lines were added in the last {period}.
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="new-arrivals-table">
            <thead>
              <tr className="border-b border-[#0a0a0a]">
                <th className="text-left bm-eyebrow text-neutral-600 py-2 pr-3 w-8 hidden md:table-cell">#</th>
                <th className="text-left bm-eyebrow text-neutral-600 py-2 pr-3">Product</th>
                <th className="text-left bm-eyebrow text-neutral-600 py-2 px-3 w-28 hidden md:table-cell">Created</th>
                <th className="text-right py-2 px-3 w-16 hidden md:table-cell"><SortHeader label="New SKUs" k="sku_count" state={sort} testId="new-arrivals-sort-sku_count" /></th>
                <th className="text-right py-2 px-2 md:px-3 w-20"><SortHeader label="Units" k="units" state={sort} testId="new-arrivals-sort-units" /></th>
                <th className="text-right py-2 px-3 w-20 hidden md:table-cell"><SortHeader label="Avg $" k="avg_price" state={sort} testId="new-arrivals-sort-avg_price" /></th>
                <th className="text-right py-2 pl-3 w-24"><SortHeader label="Revenue" k="revenue" state={sort} testId="new-arrivals-sort-revenue" /></th>
                <th className="text-left bm-eyebrow text-neutral-600 py-2 pl-3 w-[140px] hidden md:table-cell">Rel.</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r, i) => {
                const val = sort.sortKey === "revenue" ? r.revenue : sort.sortKey === "units" ? r.units : sort.sortKey === "avg_price" ? r.avg_price : r.sku_count;
                const width = Math.max(4, (val / maxSortVal) * 100);
                return (
                  <tr key={r.product_id || r.name} className="border-b border-[#0a0a0a]/15 hover:bg-[#0a0a0a]/5" data-testid={`new-arrivals-row-${i}`}>
                    <td className="py-2 pr-3 font-mono-bm text-xs text-neutral-500 align-middle hidden md:table-cell">{String(i + 1).padStart(2, "0")}</td>
                    <td className="py-2 pr-3 align-middle">
                      <div className="font-medium truncate max-w-[150px] md:max-w-[320px]" title={r.name}>{r.name}</div>
                      <div className="bm-eyebrow text-neutral-500 mt-0.5">
                        {r.category}
                        <span className="md:hidden"> · {r.created_at || "—"} · {r.sku_count} SKU{r.sku_count === 1 ? "" : "s"} · avg ${r.avg_price}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3 font-mono-bm text-xs text-neutral-600 align-middle hidden md:table-cell">{r.created_at || "—"}</td>
                    <td className={`py-2 px-3 text-right font-mono-bm tabular-nums align-middle hidden md:table-cell ${sort.sortKey === "sku_count" ? "font-bold" : "text-neutral-600"}`}>{r.sku_count}</td>
                    <td className={`py-2 px-2 md:px-3 text-right font-mono-bm tabular-nums align-middle ${sort.sortKey === "units" ? "font-bold" : ""}`}>
                      {r.units.toLocaleString()}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono-bm tabular-nums align-middle hidden md:table-cell ${sort.sortKey === "avg_price" ? "font-bold" : "text-neutral-600"}`}>
                      ${r.avg_price}
                    </td>
                    <td className={`py-2 pl-3 text-right font-mono-bm tabular-nums align-middle ${sort.sortKey === "revenue" ? "font-bold" : ""}`}>
                      {fmtMoney(r.revenue)}
                    </td>
                    <td className="py-2 pl-3 align-middle hidden md:table-cell">
                      <div className="h-2 bg-[#0a0a0a]/10 relative">
                        <div className="h-full bg-emerald-700" style={{ width: `${width}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
